import type { FC, Ref } from "react";

import type { ChildrenProp } from "../../props/ChildrenProp";
import { Card, type CardProps } from "../Card/Card";
import { Flex } from "../Flex/Flex";
import { Icon } from "../Icon/Icon";
import { List } from "../List/List";
import { ListRow } from "../ListRow/ListRow";
import { Text } from "../Text/Text";
import { ValueParser } from "../ValueParser/ValueParser";
import { Indicator } from "../Indicator";

export type PricingCardFeature = {
  title: string;
  included: boolean;
};

export type PricingCardProps = ChildrenProp & {
  title: string;
  subtitle?: string;
  price?: number;
  from?: boolean;
  suffix?: string;
  number?: number;
  description?: string;
  features?: PricingCardFeature[];
  additionalCost?: {
    value: number;
    suffix?: string;
  };
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
  border?: CardProps["border"];
};

export const PricingCard: FC<PricingCardProps> = ({
  title,
  subtitle,
  price = 0,
  from = false,
  suffix,
  number,
  description,
  border = "default",
  features = [],
  additionalCost,
  children,
  ref,
  ...props
}) => (
  <Card ref={ref} {...props} border={border} gap="m" padding="l">
    <Flex direction="row" gap="s" align="flex-start">
      {number && (
        <Indicator size="s" variant="secondary">
          {number}
        </Indicator>
      )}
      <Flex direction="column" gap="xxs2">
        <Text as="h3" bold variant="bodyL">
          {title}
        </Text>
        {subtitle && (
          <Text variant="bodyS" color="currentColor50">
            {subtitle}
          </Text>
        )}
      </Flex>
    </Flex>

    <Flex gap="xs" direction="column">
      <Flex gap="s" align="baseline" justify="flex-end">
        <Text variant="displayM" bold>
          {from && "v/a "}
          <ValueParser value={{ type: "money", value: price }} /> {suffix}
        </Text>
      </Flex>

      {additionalCost && (
        <Flex gap="s" align="baseline" justify="flex-end">
          <Text>
            +{" "}
            <ValueParser
              value={{ type: "money", value: additionalCost.value }}
            />{" "}
            {additionalCost.suffix}
          </Text>
        </Flex>
      )}
    </Flex>

    {description && (
      <Flex grow>
        <Text color="currentColor50" align="center">
          {description}
        </Text>
      </Flex>
    )}

    {features && features.length > 0 && (
      <List>
        {features.map((feature, index) => (
          <ListRow
            key={index}
            variant="compact"
            textVariant="bodyS"
            leading={
              feature.included ? (
                <Icon icon="check" color="success" />
              ) : (
                <Icon icon="close" color="error" />
              )
            }
          >
            {feature.title}
          </ListRow>
        ))}
      </List>
    )}

    {children}
  </Card>
);
